/**
 * Gift With Purchase & Cart Recommendations Tracking
 * GA4 + Elevar Compatible
 * 
 * This script tracks:
 * 1. GWP gift selections
 * 2. Product recommendation clicks
 * 3. Recommendation-driven add-to-cart conversions
 * 4. Recommendation impression tracking
 */

class CartTrackingGA4 {
  constructor() {
    this.debug = false; // Set to true for console logging
    this.elevarEnabled = this.checkElevarPresence();
    this.lastCartSignalAt = 0;
    
    // Wait for DOM and any async scripts
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.log('Initializing Cart Tracking...');
    this.initGWPTracking();
    this.initRecommendationTracking();
    this.initAddToCartTracking();
    this.initViewItemListTracking();
  }

  /**
   * Check if Elevar is present on the page
   */
  checkElevarPresence() {
    const hasElevar = !!(
      window.ElevarInvalidateContext || 
      window.ElevarDataLayer || 
      window.elevarGtmSuite ||
      (window.dataLayer && window.dataLayer.some(item => item.elevar))
    );
    this.log(`Elevar detected: ${hasElevar}`);
    return hasElevar;
  }

  /**
   * Logging helper
   */
  log(...args) {
    if (this.debug) {
      // console.log('[CartTracking]', ...args);
    }
  }

  /**
   * Core event sending method - compatible with both Elevar and standard GA4
   */
  sendGA4Event(eventName, eventParams = {}) {
    window.dataLayer = window.dataLayer || [];
    
    // Standard GA4 e-commerce events
    const ecommerceEvents = [
      'select_item', 
      'view_item_list', 
      'add_to_cart', 
      'select_promotion',
      'view_promotion'
    ];

    const isEcommerceEvent = ecommerceEvents.includes(eventName);

    if (isEcommerceEvent) {
      // Format for GA4 Enhanced Ecommerce
      const ecommercePayload = {
        event: eventName,
        ecommerce: {
          currency: eventParams.currency || this.getCurrency(),
          value: eventParams.value || this.calculateValue(eventParams.items),
          items: eventParams.items || []
        }
      };

      // Add optional e-commerce fields
      if (eventParams.item_list_id) {
        ecommercePayload.ecommerce.item_list_id = eventParams.item_list_id;
      }
      if (eventParams.item_list_name) {
        ecommercePayload.ecommerce.item_list_name = eventParams.item_list_name;
      }
      if (eventParams.promotion_id) {
        ecommercePayload.ecommerce.promotion_id = eventParams.promotion_id;
      }
      if (eventParams.promotion_name) {
        ecommercePayload.ecommerce.promotion_name = eventParams.promotion_name;
      }
      if (eventParams.creative_name) {
        ecommercePayload.ecommerce.creative_name = eventParams.creative_name;
      }
      if (eventParams.creative_slot) {
        ecommercePayload.ecommerce.creative_slot = eventParams.creative_slot;
      }
      if (eventParams.location_id) {
        ecommercePayload.ecommerce.location_id = eventParams.location_id;
      }

      // Add custom parameters at root level (not in ecommerce object)
      const customParams = { ...eventParams };
      delete customParams.currency;
      delete customParams.value;
      delete customParams.items;
      delete customParams.item_list_id;
      delete customParams.item_list_name;
      delete customParams.promotion_id;
      delete customParams.promotion_name;
      delete customParams.creative_name;
      delete customParams.creative_slot;
      delete customParams.location_id;

      Object.assign(ecommercePayload, customParams);

      this.log(`Sending ${eventName} event:`, ecommercePayload);
      window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
      window.dataLayer.push(ecommercePayload);

    } else {
      // Non-ecommerce custom event
      const payload = {
        event: eventName,
        ...eventParams
      };
      
      this.log(`Sending custom event ${eventName}:`, payload);
      window.dataLayer.push(payload);
    }

    // If Elevar is present, also send via their format (if different)
    if (this.elevarEnabled && window.ElevarDataLayer) {
      try {
        window.ElevarDataLayer.push({
          event: eventName,
          ...eventParams
        });
      } catch (e) {
        this.log('Elevar push failed:', e);
      }
    }
  }

  /**
   * Get currency from Shopify
   */
  getCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  }

  /**
   * Calculate total value from items array
   */
  calculateValue(items) {
    if (!Array.isArray(items)) return 0;
    return items.reduce((sum, item) => {
      return sum + (Number(item.price || 0) * Number(item.quantity || 1));
    }, 0);
  }

  /**
   * Format product data for GA4 items array
   */
  formatItem(data) {
    return {
      item_id: data.item_id || data.productId || '',
      item_name: data.item_name || data.productTitle || data.title || '',
      price: Number(data.price || 0),
      quantity: Number(data.quantity || 1),
      item_list_id: data.item_list_id || undefined,
      item_list_name: data.item_list_name || undefined,
      item_variant: data.item_variant || data.variantId || undefined,
      index: data.index || data.position || undefined
    };
  }

  /**
   * GIFT WITH PURCHASE TRACKING
   */
  initGWPTracking() {
    document.addEventListener('click', (e) => {
      const gwpButton = e.target.closest('[data-add-gift-gwp]');
      if (!gwpButton) return;

      const giftOption = gwpButton.closest('.gift-option');
      if (!giftOption) return;

      const productId = giftOption.getAttribute('data-product-id');
      const variantId = giftOption.getAttribute('data-variant-id');
      const giftTitle = giftOption.querySelector('.gift-title')?.textContent || 'Unknown Gift';
      const currentTier = document.querySelector('[data-current-tier]')?.getAttribute('data-current-tier') || '0';
      const isDefault = giftOption.getAttribute('data-is-default') === 'true';

      this.log('GWP gift selected:', { productId, giftTitle, currentTier });

      // GA4 Standard Event - Select Promotion
      this.sendGA4Event('select_promotion', {
        promotion_id: `gwp_tier_${currentTier}`,
        promotion_name: `Gift With Purchase - Tier ${currentTier}`,
        creative_name: giftTitle,
        creative_slot: isDefault ? 'default_option' : 'alternate_option',
        location_id: 'cart_drawer',
        items: [this.formatItem({
          item_id: productId,
          item_name: giftTitle,
          item_variant: variantId,
          price: 0,
          quantity: 1
        })]
      });

      // Custom event for detailed segmentation
      this.sendGA4Event('gwp_gift_interaction', {
        gift_tier: currentTier,
        gift_name: giftTitle,
        gift_product_id: productId,
        gift_variant_id: variantId,
        is_default_selection: isDefault,
        interaction_type: 'selection'
      });
    });
  }

  /**
   * PRODUCT RECOMMENDATION CLICK TRACKING
   */
  initRecommendationTracking() {
    // Deduplication
    this.recentClicks = new Set();

    document.addEventListener('click', (e) => {
      // Find product links or quick-add buttons in recommendations
      const recLink = e.target.closest(
        '.product-recommendations a[href*="/products/"], ' +
        '.product-recommendations--mobile a[href*="/products/"], ' +
        '.recommendations-track a[href*="/products/"], ' +
        '.recommendations-track--mobile a[href*="/products/"]'
      );
      
      const quickAddButton = e.target.closest(
        '.product-recommendations button.quick-add__submit, ' +
        '.product-recommendations--mobile button.quick-add__submit, ' +
        '.recommendations-track button.quick-add__submit, ' +
        '.recommendations-track--mobile button.quick-add__submit, ' +
        '.product-recommendations [class*="quick-add"] button[type="submit"], ' +
        '.recommendations-track [class*="quick-add"] button[type="submit"]'
      );
      
      if (!recLink && !quickAddButton) return;

      const clickedElement = recLink || quickAddButton;
      const clickType = recLink ? 'product_link' : 'quick_add';

      // Get product card container
      const slideElement = clickedElement.closest('.recommendation-slide') || 
                          clickedElement.closest('.recommendation-item') ||
                          clickedElement.closest('li') ||
                          clickedElement.closest('.card-wrapper') ||
                          clickedElement.closest('.card');
      
      if (!slideElement) return;

      // Extract product information
      const productData = this.extractProductData(slideElement, clickedElement, clickType);
      
      if (!productData.item_id && !productData.productHandle) {
        this.log('Could not extract product ID, skipping tracking');
        return;
      }

      // Deduplicate rapid clicks
      const clickKey = `${productData.item_id}_${Date.now()}`;
      if (this.recentClicks.has(clickKey)) return;
      this.recentClicks.add(clickKey);
      setTimeout(() => this.recentClicks.delete(clickKey), 1000);

      this.log('Recommendation clicked:', productData);

      // Determine device type
      const isMobile = clickedElement.closest('.product-recommendations--mobile, .recommendations-track--mobile') !== null;
      const listId = isMobile ? 'cart_recommendations_mobile' : 'cart_recommendations_desktop';
      const listName = isMobile ? 'Cart Recommendations (Mobile)' : 'Cart Recommendations (Desktop)';

      // GA4 Standard Event - Select Item
      this.sendGA4Event('select_item', {
        item_list_id: listId,
        item_list_name: listName,
        items: [this.formatItem({
          item_id: productData.item_id || productData.productHandle,
          item_name: productData.productTitle,
          price: productData.price,
          index: productData.position,
          item_list_id: listId,
          item_list_name: listName
        })]
      });

      // Store data for conversion tracking (if quick-add)
      if (quickAddButton) {
        sessionStorage.setItem('rec_conversion_pending', JSON.stringify({
          productId: productData.item_id || productData.productHandle,
          productTitle: productData.productTitle,
          price: productData.price,
          position: productData.position,
          isMobile: isMobile,
          listId: listId,
          listName: listName,
          timestamp: Date.now()
        }));
      }
    });
  }

  /**
   * Extract product data from DOM element
   */
  extractProductData(slideElement, clickedElement, clickType) {
    const productCard = slideElement.querySelector('[data-product-id]') || slideElement;
    
    // Get product ID
    let productId = productCard.getAttribute('data-product-id') || '';
    
    // Try to get from URL
    const productLink = slideElement.querySelector('a[href*="/products/"]');
    let productUrl = productLink?.getAttribute('href') || '';
    let productHandle = '';
    
    if (productUrl) {
      const handleMatch = productUrl.match(/\/products\/([^/?]+)/);
      if (handleMatch) {
        productHandle = handleMatch[1];
        if (!productId) productId = productHandle;
      }
    }
    
    // Get product title and clean it
    const titleElement = slideElement.querySelector('.card__heading, .product-title, h3, h4');
    let rawTitle = titleElement?.textContent || 
                   productLink?.getAttribute('title') || 
                   productLink?.getAttribute('aria-label') ||
                   clickedElement.getAttribute('aria-label') || 
                   'Unknown Product';
    
    const productTitle = rawTitle
      .replace(/\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/(.+?)\s+\1/g, '$1'); // Remove duplicates
    
    // Get price
    let price = 0;
    const priceElement = slideElement.querySelector('.price-item--regular, .price__regular, [class*="price"]');
    if (priceElement) {
      const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
      price = parseFloat(priceText) || 0;
    }
    
    // Get position in slider
    let position = 0;
    const trackElement = clickedElement.closest('.recommendations-track, .recommendations-track--mobile, ul');
    if (trackElement) {
      const slides = Array.from(trackElement.children);
      const slideParent = clickedElement.closest('.recommendation-slide, li, .card-wrapper');
      if (slideParent) {
        position = slides.indexOf(slideParent) + 1;
      }
    }

    return {
      item_id: productId,
      productHandle: productHandle,
      productTitle: productTitle,
      productUrl: productUrl,
      price: price,
      position: position,
      clickType: clickType
    };
  }

  /**
   * RECOMMENDATION CONVERSION TRACKING
   * Fires when a product from recommendations is added to cart
   */
  initAddToCartTracking() {
    // Listen for cart updates
    const handleCartSignal = () => {
      this.lastCartSignalAt = Date.now();
      this.checkRecommendationConversion();
    };

    document.addEventListener('cart:updated', handleCartSignal);
    document.addEventListener('cart-drawer:updated', handleCartSignal);
    
    // Fallback: Watch for cart fetch requests
    this.watchCartUpdates();
  }

  checkRecommendationConversion() {
    const pending = sessionStorage.getItem('rec_conversion_pending');
    if (!pending) return;
    
    const recData = JSON.parse(pending);
    
    // Only track if click happened within last 5 seconds
    if (Date.now() - recData.timestamp > 5000) {
      sessionStorage.removeItem('rec_conversion_pending');
      return;
    }

    this.log('Recommendation conversion detected:', recData);

    // GA4 Standard Event - Add to Cart (with list attribution)
    this.sendGA4Event('add_to_cart', {
      currency: this.getCurrency(),
      value: recData.price || 0,
      items: [this.formatItem({
        item_id: recData.productId,
        item_name: recData.productTitle,
        price: recData.price || 0,
        quantity: 1,
        item_list_id: recData.listId,
        item_list_name: recData.listName,
        index: recData.position
      })]
    });

    // Custom event for attribution reporting
    this.sendGA4Event('recommendation_conversion', {
      conversion_source: 'cart_recommendations',
      conversion_type: 'add_to_cart',
      product_id: recData.productId,
      product_name: recData.productTitle,
      device_type: recData.isMobile ? 'mobile' : 'desktop',
      recommendation_position: recData.position,
      value: recData.price || 0
    });

    // Clear the pending conversion
    sessionStorage.removeItem('rec_conversion_pending');
  }

  /**
   * Watch for cart update requests
   */
  watchCartUpdates() {
    // Already handled in theme.liquid with fetch wrapper
    // This is a backup for themes that don't have it
    if (window.__CART_TRACKING_FETCH_WRAPPED) return;
    
    const originalFetch = window.fetch;
    const tracker = this;
    window.fetch = function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
      const isCartMutation = url && (url.includes('/cart/add') || url.includes('cart/update'));
      let sentByRegiosDiscounts = false;

      try {
        const headers = args[1]?.headers || args[0]?.headers;
        let headerValue;
        if (typeof headers?.get === 'function') {
          headerValue = headers.get('x-sent-by-app');
        } else if (Array.isArray(headers)) {
          headerValue = headers.find(([name]) => name?.toLowerCase() === 'x-sent-by-app')?.[1];
        } else if (headers) {
          headerValue = Object.entries(headers)
            .find(([name]) => name.toLowerCase() === 'x-sent-by-app')?.[1];
        }
        sentByRegiosDiscounts = typeof headerValue === 'string'
          && headerValue.trim().toLowerCase() === 'regios-discounts';
      } catch (_error) {
        // Some request implementations expose headers through an unreadable proxy.
      }
      
      return originalFetch.apply(this, args).then(response => {
        if (isCartMutation && !sentByRegiosDiscounts && response.ok) {
          setTimeout(() => {
            // Most theme cart paths already emit a richer event with cartData.
            // Keep this only as a true fallback for legacy callers.
            if (Date.now() - tracker.lastCartSignalAt < 300) return;

            tracker.lastCartSignalAt = Date.now();
            tracker.checkRecommendationConversion();
          }, 150);
        }
        return response;
      });
    };
    
    window.__CART_TRACKING_FETCH_WRAPPED = true;
  }

  /**
   * VIEW ITEM LIST TRACKING
   * Fires when recommendation carousel is viewed
   */
  initViewItemListTracking() {
    this.trackedLists = new Set();

    const trackRecommendationView = () => {
      // Track desktop recommendations
      const desktopContainer = document.querySelector('.product-recommendations:not(.product-recommendations--mobile)');
      if (desktopContainer && !this.trackedLists.has('desktop')) {
        this.pollForRecommendations(desktopContainer, 'desktop');
      }
      
      // Track mobile recommendations
      const mobileContainer = document.querySelector('.product-recommendations--mobile');
      if (mobileContainer && !this.trackedLists.has('mobile')) {
        this.pollForRecommendations(mobileContainer, 'mobile');
      }
    };

    // Initial check
    setTimeout(trackRecommendationView, 500);

    // Track when cart drawer opens
    document.addEventListener('cart-drawer:opened', trackRecommendationView);
    document.addEventListener('cart:updated', trackRecommendationView);

    // Use IntersectionObserver for lazy-loaded recommendations
    this.observeRecommendations();
  }

  /**
   * Poll for recommendations to load (they load async)
   */
  pollForRecommendations(container, deviceType, attempts = 0, maxAttempts = 10) {
    if (attempts >= maxAttempts) {
      this.log(`Max polling attempts reached for ${deviceType} recommendations`);
      return;
    }
    
    // More flexible selector approach
    const track = container.querySelector('.recommendations-track, #recommendationsTrack, .product-grid, [class*="recommendations"]');
    
    if (!track) {
      this.log(`No track found on attempt ${attempts + 1} for ${deviceType}`);
      setTimeout(() => {
        this.pollForRecommendations(container, deviceType, attempts + 1, maxAttempts);
      }, 300);
      return;
    }
    
    // Look for slides OR product cards OR any items with product data
    const slides = track.querySelectorAll('.recommendation-slide, .product-card, .card-wrapper, li[class*="product"]');
    const cardsWithIds = track.querySelectorAll('[data-product-id], .card[id*="card-product"]');
    
    this.log(`Attempt ${attempts + 1} for ${deviceType}: Found ${slides.length} slides and ${cardsWithIds.length} cards with IDs`);
    
    // Success condition: either we have slides with IDs, or we have cards
    if ((slides.length > 0 && cardsWithIds.length > 0) || cardsWithIds.length >= 3) {
      this.trackRecommendationList(container, deviceType);
    } else {
      // Keep trying
      setTimeout(() => {
        this.pollForRecommendations(container, deviceType, attempts + 1, maxAttempts);
      }, 300);
    }
  }

  /**
   * Track impression of recommendation list
   */
trackRecommendationList(container, deviceType) {
  if (this.trackedLists.has(deviceType)) return;
  
  const track = container.querySelector('.recommendations-track, #recommendationsTrack, .product-grid, [class*="recommendations"]') || container;
  
  // More flexible slide/card selection
  let slides = track.querySelectorAll('.recommendation-slide');
  
  // Fallback: look for any product cards
  if (slides.length === 0) {
    slides = track.querySelectorAll('.product-card, .card-wrapper, li[class*="product"], .card[data-product-id]');
  }
  
  // Last resort: any element with data-product-id
  if (slides.length === 0) {
    slides = track.querySelectorAll('[data-product-id]');
  }
  
  if (slides.length === 0) {
    this.log(`No slides found for ${deviceType} recommendations after all attempts`);
    return;
  }

  const listId = deviceType === 'mobile' ? 'cart_recommendations_mobile' : 'cart_recommendations_desktop';
  const listName = deviceType === 'mobile' ? 'Cart Recommendations (Mobile)' : 'Cart Recommendations (Desktop)';

  const items = Array.from(slides).map((slide, index) => {
    // Try multiple ways to find product data
    let productId = slide.getAttribute('data-product-id');
    
    if (!productId) {
      const card = slide.querySelector('[data-product-id]');
      productId = card?.getAttribute('data-product-id');
    }
    
    if (!productId) {
      // Try to extract from links
      const link = slide.querySelector('a[href*="/products/"]');
      if (link) {
        const match = link.href.match(/\/products\/([^/?]+)/);
        if (match) productId = match[1];
      }
    }
    
    if (!productId) {
      this.log(`Skipping slide ${index + 1}, no product ID found`);
      return null;
    }

    // Get title
    const titleElement = slide.querySelector('.card__heading, .product-title, .card-information__text, h3, h4, a[href*="/products/"]');
    let productTitle = titleElement?.textContent || titleElement?.getAttribute('title') || titleElement?.getAttribute('aria-label') || '';
    
    productTitle = productTitle
      .replace(/\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/(.+?)\s+\1/g, '$1');
    
    if (!productTitle) {
      this.log(`Skipping slide ${index + 1}, no product title found`);
      return null;
    }

    // Get price
    let price = 0;
    const priceElement = slide.querySelector('.price-item--regular, .price__regular, .price, [class*="price"]');
    if (priceElement) {
      const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
      price = parseFloat(priceText) || 0;
    }

    return this.formatItem({
      item_id: productId,
      item_name: productTitle,
      price: price,
      index: index + 1,
      item_list_id: listId,
      item_list_name: listName
    });
  }).filter(Boolean);

  if (items.length > 0) {
    this.log(`Tracking view_item_list for ${deviceType}:`, items);
    
    // GA4 Standard Event - View Item List
    this.sendGA4Event('view_item_list', {
      item_list_id: listId,
      item_list_name: listName,
      items: items
    });

    this.trackedLists.add(deviceType);
    container.setAttribute('data-tracked', 'true');
  } else {
    this.log(`No valid items found for ${deviceType} after extraction`);
  }
}

  /**
   * Use IntersectionObserver to track when recommendations come into view
   */
  observeRecommendations() {
    if (!window.IntersectionObserver) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const container = entry.target;
          const isMobile = container.classList.contains('product-recommendations--mobile');
          const deviceType = isMobile ? 'mobile' : 'desktop';
          
          if (!this.trackedLists.has(deviceType)) {
            this.pollForRecommendations(container, deviceType);
          }
        }
      });
    }, {
      threshold: 0.5, // Track when 50% visible
      rootMargin: '0px'
    });

    // Observe recommendation containers
    const containers = document.querySelectorAll('.product-recommendations, .product-recommendations--mobile');
    containers.forEach(container => observer.observe(container));
  }
}

// Initialize tracking
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartTrackingGA4 = new CartTrackingGA4();
  });
} else {
  window.cartTrackingGA4 = new CartTrackingGA4();
}
